// How the web app calls the Application API: JSON over HTTP to the same origin, and what a refusal looks like.

/** An answer the API refused, with the problems it named. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly problems: readonly string[] = [],
    /** What to do next, when the API says: never to change what the problems name if the person cannot. */
    readonly recovery?: string,
    /** The sensitive action to step up for, when the API answers `step_up_required`. */
    readonly action?: string,
    /** Whether the person's PIN may confirm that step-up, as the API says; only a code when it does not say. */
    readonly pinAllowed?: boolean,
  ) {
    super(problems[0] ?? code);
    this.name = "ApiError";
  }
}

export type Fetch = typeof fetch;

export async function call<T>(fetcher: Fetch, path: string, init?: { method?: string; json?: unknown }): Promise<T> {
  const response = await fetcher(path, {
    method: init?.method ?? (init?.json === undefined ? "GET" : "POST"),
    credentials: "same-origin",
    headers: init?.json === undefined ? {} : { "content-type": "application/json" },
    ...(init?.json === undefined ? {} : { body: JSON.stringify(init.json) }),
  });
  if (response.status === 204) return undefined as T;
  const body = (await response.json().catch(() => ({}))) as Refusal;
  if (!response.ok) throw refusal(response.status, body);
  return body as T;
}

/** What the API says when it refuses. */
export interface Refusal {
  readonly error?: string;
  readonly problems?: string[];
  readonly recovery?: string;
  readonly action?: string;
  readonly pin_allowed?: boolean;
}

export const refusal = (status: number, body: Refusal): ApiError =>
  new ApiError(status, body.error ?? "failed", body.problems ?? [], body.recovery, body.action, body.pin_allowed);
